import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { fmtCurrency } from "../lib/format";
import { formatPeriodRange } from "./PeriodBadge";
import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { cn } from "../lib/utils";

const money = (v) => (
  <span className={cn("font-mono tabular-nums", v < 0 ? "text-red-600" : "text-foreground")}>{fmtCurrency(v)}</span>
);

const ChangeCell = ({ current, last, invert }) => {
  if (invert) {
    // Expense rows: compare absolute spend
    const spendDelta = Math.abs(current) - Math.abs(last);
    if (Math.abs(spendDelta) < 0.005) {
      return (
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          <Minus className="h-3.5 w-3.5" /> No change
        </span>
      );
    }
    const more = spendDelta > 0;
    return (
      <span className={cn("inline-flex items-center gap-1 font-medium", more ? "text-red-600" : "text-emerald-600")}>
        {more ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
        <span className="font-mono tabular-nums">{fmtCurrency(Math.abs(spendDelta))}</span>
        {more ? "more spend" : "less spend"}
      </span>
    );
  }
  const delta = current - last;
  if (Math.abs(delta) < 0.005) {
    return (
      <span className="inline-flex items-center gap-1 text-muted-foreground">
        <Minus className="h-3.5 w-3.5" /> No change
      </span>
    );
  }
  const up = delta > 0;
  return (
    <span className={cn("inline-flex items-center gap-1 font-medium", up ? "text-emerald-600" : "text-red-600")}>
      {up ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
      <span className="font-mono tabular-nums">{fmtCurrency(Math.abs(delta))}</span>
    </span>
  );
};

/**
 * Compares the current week's figures with the most recent archived week.
 * rows: [{ label, current, last, invert }] where invert marks expense rows.
 */
export const WeekComparison = ({ lastWeek, rows, testId }) => {
  if (!lastWeek) {
    return (
      <Card className="shadow-sm" data-testid={testId}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">Week Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No archived week to compare against yet. The first comparison appears after the first weekly reset
            (automatic every Tuesday, or manual via Reset Week).
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm" data-testid={testId}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">Week Comparison</CardTitle>
        <p className="text-xs text-muted-foreground">
          vs last archived week: {formatPeriodRange(lastWeek.week_start, lastWeek.week_end)}
        </p>
      </CardHeader>
      <CardContent>
        <div className="w-full overflow-x-auto rounded-md border border-border">
          <Table className="min-w-[640px]">
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Metric</TableHead>
                <TableHead className="text-right">This week</TableHead>
                <TableHead className="text-right">Last week</TableHead>
                <TableHead>Change</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.label} data-testid={`${testId}-row-${r.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <TableCell className="font-medium">{r.label}</TableCell>
                  <TableCell className="text-right">{money(r.current)}</TableCell>
                  <TableCell className="text-right">{money(r.last)}</TableCell>
                  <TableCell>
                    <ChangeCell current={r.current} last={r.last} invert={r.invert} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
